import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, History, Plus } from "lucide-react";
import { toast } from "sonner";

import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, toastError } from "../../lib/api";
import { useEffectiveRole } from "../../lib/roles";

type Patient = {
  referral_id: string; patient_contact_id: string; patient_name: string;
  phone?: string | null; email?: string | null; referral_status: string;
};
type Assignee = { id: string; name: string; role: string };
type Note = { id: string; note: string; created_at: string; created_by_name: string };
type HistoryItem = {
  action: string; from_status?: string | null; to_status?: string | null;
  occurred_at: string; actor_name: string;
};
type PatientEvent = {
  id: string; referral_id: string; patient_name: string; patient_phone?: string | null;
  assigned_to_user_id: string; assigned_to_name: string; start_at: string; end_at?: string | null;
  purpose: string; status: string; notes: Note[]; history: HistoryItem[]; version: number;
};
const STATUSES = ["scheduled", "completed", "rescheduled", "cancelled", "no_answer"];
const statusLabel = (value: string) => value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
export const canAccessPatientCalendar = (role: string | null) => role === "owner_admin" || role === "admin_support";

type ViewMode = "day" | "week" | "month";
const blank = { referral_id: "", assigned_to_user_id: "", start_at: "", end_at: "", purpose: "", notes: "" };

const startOfDay = (value: Date) => {
  const next = new Date(value); next.setHours(0, 0, 0, 0); return next;
};
const addDays = (value: Date, amount: number) => {
  const next = new Date(value); next.setDate(next.getDate() + amount); return next;
};
const startOfWeek = (value: Date) => addDays(startOfDay(value), -value.getDay());
export const calendarDaysForView = (view: ViewMode, cursor: Date) => {
  if (view === "day") return [startOfDay(cursor)];
  if (view === "week") return Array.from({ length: 7 }, (_, index) => addDays(startOfWeek(cursor), index));
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
};
const dateKey = (value: string | Date) => {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export default function PatientCalendar() {
  const role = useEffectiveRole();
  const allowed = canAccessPatientCalendar(role);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [events, setEvents] = useState<PatientEvent[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [view, setView] = useState<ViewMode>("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<PatientEvent | null>(null);
  const [history, setHistory] = useState<PatientEvent[]>([]);
  const [form, setForm] = useState(blank);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const calendarDays = useMemo(() => calendarDaysForView(view, cursor), [view, cursor]);
  const rangeStart = calendarDays[0];
  const rangeEnd = useMemo(() => {
    const end = new Date(calendarDays[calendarDays.length - 1]); end.setHours(23, 59, 59, 999); return end;
  }, [calendarDays]);

  const load = useCallback(async () => {
    if (!allowed) return;
    try {
      const [{ data: eventData }, { data: patientData }, { data: assigneeData }] = await Promise.all([
        api.get("/patient-calendar/events", { params: { status: status === "all" ? "" : status, from_utc: rangeStart.toISOString(), to_utc: rangeEnd.toISOString() } }),
        api.get("/patient-calendar/patients", { params: { q: search } }),
        api.get("/patient-calendar/assignees"),
      ]);
      setEvents(eventData.items || []);
      setPatients(patientData.items || []);
      setAssignees(assigneeData.items || []);
    } catch (error) { toastError(error); }
  }, [allowed, search, status, rangeStart.getTime(), rangeEnd.getTime()]);

  useEffect(() => { void load(); }, [load]);

  const eventsByDate = useMemo(() => {
    const rows = new Map<string, PatientEvent[]>();
    const query = search.trim().toLowerCase();
    for (const event of events) {
      if (query && ![event.patient_name, event.patient_phone, event.purpose, event.assigned_to_name]
        .some((value) => String(value || "").toLowerCase().includes(query))) continue;
      const key = dateKey(event.start_at);
      rows.set(key, [...(rows.get(key) || []), event]);
    }
    return rows;
  }, [events, search]);

  const move = (amount: number) => {
    if (view === "day") setCursor((current) => addDays(current, amount));
    else if (view === "week") setCursor((current) => addDays(current, amount * 7));
    else setCursor((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
  };
  const headerLabel = view === "day"
    ? cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : view === "week"
      ? `${calendarDays[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${calendarDays[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
      : cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const create = async () => {
    if (!form.referral_id || !form.assigned_to_user_id || !form.start_at || !form.purpose.trim()) {
      toast.error("Patient, assignee, date/time, and purpose are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/patient-calendar/events", {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        notes: form.notes.trim() || null,
      });
      toast.success("Patient schedule created");
      setOpen(false); setForm(blank); await load();
    } catch (error) { toastError(error); } finally { setSaving(false); }
  };

  const updateStatus = async (event: PatientEvent, nextStatus: string) => {
    try {
      const { data } = await api.patch(`/patient-calendar/events/${event.id}`, {
        if_version: event.version, status: nextStatus,
      });
      toast.success("Schedule status updated");
      setDetail(data); await load();
    } catch (error) { toastError(error); }
  };

  const addNote = async () => {
    if (!detail || !note.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/patient-calendar/events/${detail.id}/notes`, { note: note.trim() });
      setDetail(data); setNote(""); toast.success("Timestamped note added"); await load();
    } catch (error) { toastError(error); } finally { setSaving(false); }
  };

  const openHistory = async (patient: Patient) => {
    try {
      const { data } = await api.get(`/patient-calendar/referrals/${patient.referral_id}/history`);
      setHistory(data.items || []);
    } catch (error) { toastError(error); }
  };

  if (!allowed) return <EmptyState icon={CalendarDays} title="Patient Calendar is restricted" description="Only Tenant/Owner/Admin and Admin Support can access patient scheduling." />;

  return <>
    <PageHeader
      title="Patient Calendar"
      subtitle="Non-clinical follow-up scheduling for existing referred patients"
      actions={<Button onClick={() => setOpen(true)} data-testid="add-patient-schedule"><Plus className="mr-1 h-4 w-4" /> Add schedule</Button>}
    />
    <main className="mx-auto max-w-7xl space-y-5 p-6 md:p-8">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        This calendar is separate from the clinical EMR calendar and does not change commissions, reimbursements, or invoices.
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_220px]">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search referred patient by name, phone, or email..." />
        <Select value={status} onValueChange={setStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {STATUSES.map((item) => <SelectItem key={item} value={item}>{statusLabel(item)}</SelectItem>)}
        </SelectContent></Select>
      </div>

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="patient-calendar-grid">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => move(-1)} aria-label="Previous period"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
            <Button variant="outline" size="sm" onClick={() => move(1)} aria-label="Next period"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <h2 className="text-base font-semibold text-slate-900">{headerLabel}</h2>
          <div className="flex rounded-md border border-slate-200 p-1">
            {(["day", "week", "month"] as ViewMode[]).map((mode) => <button key={mode} onClick={() => setView(mode)}
              className={`rounded px-3 py-1.5 text-xs font-medium ${view === mode ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`patient-calendar-view-${mode}`}>{statusLabel(mode)}</button>)}
          </div>
        </header>
        {view !== "day" && <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <div key={day} className="p-2 text-center text-xs font-semibold text-slate-500">{day}</div>)}
        </div>}
        <div className={view === "day" ? "grid grid-cols-1" : "grid grid-cols-7"}>
          {calendarDays.map((day) => {
            const items = eventsByDate.get(dateKey(day)) || [];
            const outsideMonth = view === "month" && day.getMonth() !== cursor.getMonth();
            const today = dateKey(day) === dateKey(new Date());
            return <div key={dateKey(day)} className={`border-b border-r border-slate-200 p-2 ${view === "month" ? "min-h-32" : "min-h-72"} ${outsideMonth ? "bg-slate-50" : "bg-white"}`}>
              <div className={`mb-2 flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${today ? "bg-blue-600 text-white" : outsideMonth ? "text-slate-400" : "text-slate-700"}`}>{day.getDate()}</div>
              <div className="space-y-1.5">
                {items.map((event) => <button key={event.id} onClick={() => setDetail(event)}
                  className={`w-full rounded border p-2 text-left hover:border-blue-400 ${event.status === "completed" ? "border-emerald-200 bg-emerald-50" : event.status === "cancelled" ? "border-slate-200 bg-slate-50 opacity-60" : event.status === "no_answer" ? "border-amber-200 bg-amber-50" : "border-blue-200 bg-blue-50"}`}
                  data-testid={`patient-event-${event.id}`}>
                  <div className="truncate text-xs font-semibold">{new Date(event.start_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {event.patient_name}</div>
                  <div className="truncate text-[11px] text-slate-600">{event.purpose}</div>
                  {view !== "month" && <div className="mt-1 truncate text-[10px] text-slate-500">{event.assigned_to_name} · {statusLabel(event.status)} · {event.notes?.length || 0} notes</div>}
                </button>)}
                {items.length === 0 && view !== "month" && <div className="py-8 text-center text-xs text-slate-400">No schedules</div>}
              </div>
            </div>;
          })}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 font-semibold">Referral schedule history</h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {patients.map((patient) => <Button key={patient.referral_id} variant="outline" className="justify-start"
            onClick={() => void openHistory(patient)}><History className="mr-2 h-4 w-4" />{patient.patient_name}</Button>)}
        </div>
      </section>
    </main>

    <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-xl">
      <DialogHeader><DialogTitle>Add patient schedule</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <div><Label>Referred patient *</Label><Select value={form.referral_id} onValueChange={(v) => setForm({...form, referral_id:v})}>
          <SelectTrigger><SelectValue placeholder="Select existing referred patient" /></SelectTrigger><SelectContent>
            {patients.map((patient) => <SelectItem key={patient.referral_id} value={patient.referral_id}>{patient.patient_name}{patient.phone ? ` · ${patient.phone}` : ""}</SelectItem>)}
          </SelectContent></Select></div>
        <div><Label>Assigned to *</Label><Select value={form.assigned_to_user_id} onValueChange={(v) => setForm({...form, assigned_to_user_id:v})}>
          <SelectTrigger><SelectValue placeholder="Select Owner/Admin or Admin Support" /></SelectTrigger><SelectContent>
            {assignees.map((user) => <SelectItem key={user.id} value={user.id}>{user.name} · {statusLabel(user.role)}</SelectItem>)}
          </SelectContent></Select></div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label>Start date and time *</Label><Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({...form,start_at:e.target.value})} /></div>
          <div><Label>End date and time</Label><Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({...form,end_at:e.target.value})} /></div>
        </div>
        <div><Label>Purpose *</Label><Input value={form.purpose} onChange={(e) => setForm({...form,purpose:e.target.value})} placeholder="Replacement call, follow-up, upselling..." /></div>
        <div><Label>Initial note</Label><textarea className="min-h-24 w-full rounded-md border border-slate-200 p-3 text-sm" value={form.notes} onChange={(e) => setForm({...form,notes:e.target.value})} /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => void create()} disabled={saving}>Create schedule</Button></DialogFooter>
    </DialogContent></Dialog>

    <Dialog open={Boolean(detail)} onOpenChange={(next) => !next && setDetail(null)}><DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      {detail && <><DialogHeader><DialogTitle>{detail.patient_name} · {detail.purpose}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2 text-sm">
          <div><span className="text-slate-500">Schedule</span><div>{new Date(detail.start_at).toLocaleString()}</div></div>
          <div><span className="text-slate-500">Assigned to</span><div>{detail.assigned_to_name}</div></div>
        </div>
        <div><Label>Status</Label><Select value={detail.status} onValueChange={(value) => void updateStatus(detail, value)}>
          <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((item) => <SelectItem key={item} value={item}>{statusLabel(item)}</SelectItem>)}</SelectContent>
        </Select></div>
        <div className="space-y-2"><Label>Timestamped notes</Label>
          {(detail.notes || []).map((item) => <div key={item.id} className="rounded-md bg-slate-50 p-3 text-sm">
            <div>{item.note}</div><div className="mt-1 text-xs text-slate-500">{item.created_by_name} · {new Date(item.created_at).toLocaleString()}</div>
          </div>)}
          <textarea className="min-h-20 w-full rounded-md border border-slate-200 p-3 text-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." />
          <Button onClick={() => void addNote()} disabled={saving || !note.trim()}>Add timestamped note</Button>
        </div>
        <div className="space-y-2"><Label>Change history</Label>{(detail.history || []).slice().reverse().map((item, index) =>
          <div key={index} className="text-xs text-slate-600">{item.actor_name} · {statusLabel(item.action)} · {new Date(item.occurred_at).toLocaleString()}</div>)}</div>
      </>}
    </DialogContent></Dialog>

    <Dialog open={history.length > 0} onOpenChange={(next) => !next && setHistory([])}><DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{history[0]?.patient_name} · Schedule history</DialogTitle></DialogHeader>
      <div className="space-y-3">{history.map((event) => <div key={event.id} className="rounded-md border border-slate-200 p-3">
        <div className="flex justify-between gap-3"><strong>{event.purpose}</strong><span className="text-xs">{statusLabel(event.status)}</span></div>
        <div className="text-sm text-slate-600">{new Date(event.start_at).toLocaleString()} · {event.assigned_to_name}</div>
        <div className="mt-2 text-xs text-slate-500">{event.notes?.length || 0} timestamped notes · {event.history?.length || 0} history entries</div>
      </div>)}</div>
    </DialogContent></Dialog>
  </>;
}
